// VendorDetail right-side panel + its sub-panels (manual override, why-this-status,
// decision log, spark history). Pulled out of ExpenseAudit.jsx in the Phase G refactor.
import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

import { api, moneyFull, pct } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import {
  STATUS_COLORS, DECISION_OPTIONS, ACTIONABLE_STATUSES, CHART_TOOLTIP,
  Stat, ScoreBox, autoInitials, formatSpend,
} from './_shared';

export function VendorDetail({ vendor, onDecisionSaved, quarterKey }) {
  const { user: currentUser } = useAuth();
  const [decision, setDecision] = useState('Keep');
  const [dollarImpact, setDollarImpact] = useState(0);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!vendor) return;
    const existing = vendor.current_decision;
    setDecision(existing?.decision || vendor.suggested_status || 'Keep');
    // Default $ impact defaults per Kat's advisor feedback (Feb 2026):
    // - existing logged decision wins
    // - otherwise use the auto-computed opportunity ($/mo)
    // - otherwise for Cut/Cancel, default to the vendor's ACTUAL monthly
    //   spend from QuickBooks so history totals aren't stuck at $0
    // - otherwise 0 (Keep / Watch cases where a $ impact isn't meaningful)
    const initialDecision = existing?.decision || vendor.suggested_status || 'Keep';
    const fallbackForCut = (initialDecision === 'Cut' || initialDecision === 'Cancel')
      ? (vendor.actual_monthly || vendor.contracted_monthly || 0)
      : 0;
    setDollarImpact(existing?.dollar_impact ?? vendor.opportunity_monthly ?? fallbackForCut);
    setNote(existing?.note || '');
  }, [vendor]);

  // When the reviewer flips the decision, if they haven't hand-edited the
  // impact yet, snap it to a sensible default so Cut → full monthly spend and
  // Keep → 0. This eliminates the "everything shows $0/mo in history" bug.
  const handleDecisionChange = (next) => {
    setDecision(next);
    const existing = vendor?.current_decision;
    if (existing?.dollar_impact) return; // reviewer already set a number, respect it
    if (next === 'Cut' || next === 'Cancel') {
      setDollarImpact(vendor?.actual_monthly || vendor?.contracted_monthly || 0);
    } else if (next === 'Right-size' || next === 'Renegotiate') {
      // No auto — reviewer must set the target
      if (vendor?.opportunity_monthly) setDollarImpact(vendor.opportunity_monthly);
    } else {
      setDollarImpact(0);
    }
  };

  if (!vendor) {
    return (
      <div className="bg-white border-t-4 border-indie-navy p-5">
        <div className="label-editorial mb-3">Vendor detail</div>
        <p className="text-sm text-indie-ink/55">Click a vendor row to see spend history, scores, and log a decision.</p>
      </div>
    );
  }

  const palette = STATUS_COLORS[vendor.final_status || vendor.suggested_status || 'Watch'];
  const scores = vendor.scores || {};
  const history = vendor.actual_history || [];

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/expense-audit/decision', {
        vendor_id: vendor.id,
        vendor_name: vendor.vendor_name,
        quarter_key: quarterKey,
        decision,
        dollar_impact: Number(dollarImpact) || 0,
        note,
      });
      toast.success('Decision logged');
      onDecisionSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save decision.');
    } finally {
      setSaving(false);
    }
  };

  if (vendor.is_unmapped) {
    return (
      <div data-testid="ea-vendor-detail" className="bg-white border-t-4 border-indie-neg-dark p-5 space-y-3">
        <div className="label-editorial text-indie-neg-dark">Unmapped — needs review</div>
        <div className="text-xl font-bold text-indie-navy">{vendor.vendor_name}</div>
        <div className="text-sm text-indie-ink/75">
          QBO has logged {vendor.txn_count} transactions to this vendor averaging {moneyFull(vendor.actual_monthly)}/mo,
          but there's no matching row in the Vendor Master sheet.
        </div>
        <div className="text-xs text-indie-ink/65">
          Next step: add a row to the Vendor Master sheet, or investigate whether this charge should still exist.
        </div>
        {history.length > 1 && <SparkHistory history={history} />}
      </div>
    );
  }

  return (
    <div data-testid="ea-vendor-detail" className="bg-white border-t-4 border-indie-navy p-5 space-y-4">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-indie-ink/55">{vendor.category || '—'} · {vendor.owner || 'unowned'}</div>
          <div className="text-xl font-bold text-indie-navy">{vendor.vendor_name}</div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${palette?.bg} ${palette?.text}`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${palette?.dot}`} />
          {vendor.final_status || vendor.suggested_status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <Stat label="Contracted" value={`${moneyFull(vendor.contracted_monthly)}/mo`} />
        <Stat
          label={vendor.cadence === 'recurring' ? 'Run rate' : vendor.cadence === 'one_time' ? 'One-time' : 'Window total'}
          value={(() => { const s = formatSpend(vendor); return s.primary === '—' ? '—' : `${s.primary}`; })()}
        />
        <Stat label="Plan" value={vendor.plan_tier || '—'} />
        <Stat label="Cadence" value={vendor.billing_cadence} />
        <Stat label="Seats" value={vendor.seats_purchased ? `${vendor.seats_active}/${vendor.seats_purchased}` : '—'} />
        <Stat label="Utilization" value={vendor.utilization_pct != null ? pct(vendor.utilization_pct, 0) : (vendor.utilization_label || '—')} />
        <Stat label="Renews" value={vendor.renewal_date ? `${vendor.renewal_date} (${vendor.renewal_days_away}d)` : '—'} />
        <Stat label="Opp / mo" value={vendor.opportunity_monthly ? moneyFull(vendor.opportunity_monthly) : '—'} />
      </div>

      {/* Scores */}
      <div className="border-t border-indie-navy/10 pt-3">
        <div className="label-editorial mb-2">The four scores</div>
        <div className="grid grid-cols-4 gap-2 text-xs">
          <ScoreBox label="Spend" v={scores.spend_alignment} />
          <ScoreBox label="Util" v={scores.utilization} />
          <ScoreBox label="Redund" v={scores.redundancy} />
          <ScoreBox label="ROI" v={scores.roi} />
        </div>
      </div>

      {vendor.scope && (
        <div className="text-sm">
          <div className="text-[10px] uppercase tracking-wider text-indie-ink/55 mb-0.5">Scope</div>
          <div className="text-indie-ink/85">{vendor.scope}</div>
        </div>
      )}
      {vendor.roi_notes && (
        <div className="text-sm">
          <div className="text-[10px] uppercase tracking-wider text-indie-ink/55 mb-0.5">ROI / value notes</div>
          <div className="text-indie-ink/85">{vendor.roi_notes}</div>
        </div>
      )}
      {vendor.redundancy_notes && (
        <div className="text-sm">
          <div className="text-[10px] uppercase tracking-wider text-indie-ink/55 mb-0.5">Redundancy notes</div>
          <div className="text-indie-ink/85">{vendor.redundancy_notes}</div>
        </div>
      )}
      {vendor.audit_notes && (
        <div className="text-sm">
          <div className="text-[10px] uppercase tracking-wider text-indie-ink/55 mb-0.5">Audit notes</div>
          <div className="text-indie-ink/85">{vendor.audit_notes}</div>
        </div>
      )}

      {history.length > 1 && <SparkHistory history={history} />}

      {/* Why this status? — explainer of the auto rule + underlying data */}
      <WhyThisStatusPanel vendor={vendor} />

      {/* Core infrastructure toggle (SaaS only) — flips auto-Keep on/off */}
      {vendor.vendor_type === 'SaaS / Software' && (
        <CoreInfrastructureToggle vendor={vendor} onSaved={onDecisionSaved} />
      )}

      {/* Manual override — light-weight, persists across syncs, not quarter-tied */}
      <ManualOverridePanel vendor={vendor} onSaved={onDecisionSaved} currentUser={currentUser} />

      {/* Decision log — every change with initials and timestamp */}
      <DecisionLogPanel vendorId={vendor.id} refreshKey={vendor.manual_override?.set_at} currentUser={currentUser} onReset={onDecisionSaved} />

      {/* Decision form */}
      <div className="border-t border-indie-navy/10 pt-3">
        <div className="label-editorial mb-2">This quarter's decision ({quarterKey})</div>
        <div className="space-y-2">
          <select
            value={decision}
            onChange={(e) => handleDecisionChange(e.target.value)}
            data-testid="ea-decision-select"
            className="w-full bg-indie-cream border border-indie-navy/15 px-2 py-1.5 text-sm focus:outline-none focus:border-indie-navy"
          >
            {DECISION_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-indie-ink/55 font-semibold whitespace-nowrap">$ impact /mo</span>
            <input
              type="number"
              value={dollarImpact}
              onChange={(e) => setDollarImpact(e.target.value)}
              data-testid="ea-decision-impact"
              className="w-full bg-indie-cream border border-indie-navy/15 px-2 py-1.5 text-sm num-tabular focus:outline-none focus:border-indie-navy"
            />
          </div>
          <div className="text-[10px] text-indie-ink/50 -mt-1">
            Auto-fills with QuickBooks monthly avg ({moneyFull(vendor.actual_monthly || 0)}/mo) when you pick Cut / Cancel. Edit freely.
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (lever, next step, owner action…)"
            rows={3}
            data-testid="ea-decision-note"
            className="w-full bg-indie-cream border border-indie-navy/15 px-2 py-1.5 text-sm focus:outline-none focus:border-indie-navy"
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="ea-decision-save"
            className="w-full bg-indie-yellow text-indie-ink font-semibold uppercase tracking-wider text-xs px-3 py-2 hover:bg-indie-yellow/85 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save decision'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManualOverridePanel({ vendor, onSaved, currentUser }) {
  const existing = vendor.manual_override;
  const [status, setStatus] = useState(existing?.requested_status || existing?.manual_status || '');
  const [targetMonthly, setTargetMonthly] = useState(existing?.target_monthly ?? '');
  const [targetPct, setTargetPct] = useState(existing?.target_reduction_pct ?? '');
  const [reason, setReason] = useState(existing?.reason || '');
  const [initials, setInitials] = useState(existing?.actor_initials || autoInitials(currentUser));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const ex = vendor.manual_override;
    setStatus(ex?.requested_status || ex?.manual_status || '');
    setTargetMonthly(ex?.target_monthly ?? '');
    setTargetPct(ex?.target_reduction_pct ?? '');
    setReason(ex?.reason || '');
    setInitials(ex?.actor_initials || autoInitials(currentUser));
  }, [vendor?.id, vendor.manual_override, currentUser]);

  const hasOverride = Boolean(existing);
  const autoStatus = vendor.suggested_status || 'Watch';
  const palette = STATUS_COLORS[autoStatus] || STATUS_COLORS.Watch;
  const needsTarget = ACTIONABLE_STATUSES.includes(status) && !['Cut', 'Cancel'].includes(status);
  const hasTarget = (targetMonthly !== '' && targetMonthly != null)
    || (targetPct !== '' && targetPct != null);
  const reasonRemaining = 200 - (reason || '').length;

  // Live-preview Opp/mo from the current target inputs so the reviewer can
  // see the math before saving. Mirror of the backend formula.
  const actual = vendor.actual_monthly || 0;
  const previewOpp = (() => {
    if (status === 'Cut' || status === 'Cancel') return actual;
    if (status === 'Right-size' || status === 'Renegotiate') {
      if (targetMonthly !== '' && targetMonthly != null) {
        return Math.max(0, actual - Number(targetMonthly));
      }
      if (targetPct !== '' && targetPct != null) {
        return actual * Number(targetPct) / 100;
      }
      return null;
    }
    return null;
  })();

  const save = async () => {
    if (!status) return;
    setSaving(true);
    try {
      const res = await api.post('/expense-audit/vendor-override', {
        vendor_id: vendor.id,
        vendor_name: vendor.vendor_name,
        manual_status: status,
        target_monthly: targetMonthly === '' ? null : Number(targetMonthly),
        target_reduction_pct: targetPct === '' ? null : Number(targetPct),
        reason,
        initials: initials || null,
        actual_monthly: actual,
      });
      const eff = res.data?.override?.manual_status;
      if (eff && eff !== status) {
        toast.success(`${vendor.vendor_name}: requested ${status} → effective ${eff} (needs target)`);
      } else {
        toast.success(`${vendor.vendor_name}: ${eff || status}`);
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save override.');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.post('/expense-audit/vendor-override', {
        vendor_id: vendor.id,
        vendor_name: vendor.vendor_name,
        manual_status: null,
      });
      setStatus('');
      setTargetMonthly('');
      setTargetPct('');
      setReason('');
      toast.success(`${vendor.vendor_name}: override cleared`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not clear override.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-indie-navy/10 pt-3" data-testid="ea-manual-override-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="label-editorial">Manual status override</div>
        {hasOverride && (
          <span className="text-[10px] uppercase tracking-wider font-semibold text-indie-yellow bg-indie-ink px-1.5 py-0.5">
            Manual · {existing.actor_initials || 'JV'}
          </span>
        )}
      </div>
      <div className="text-[11px] text-indie-ink/60 mb-2">
        Auto-suggested:{' '}
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${palette.bg} ${palette.text}`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${palette.dot}`} />
          {autoStatus}
        </span>
      </div>
      <div className="space-y-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="ea-override-select"
          className="w-full bg-indie-cream border border-indie-navy/15 px-2 py-1.5 text-sm focus:outline-none focus:border-indie-navy"
        >
          <option value="">— No override (use auto) —</option>
          {DECISION_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>

        {needsTarget && (
          <div className="bg-indie-cream/60 border border-indie-navy/10 p-2 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-indie-ink/60 font-semibold">
              Target spend — required for {status}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-indie-ink/55 mb-0.5">Target $/mo</div>
                <input
                  type="number"
                  value={targetMonthly}
                  onChange={(e) => { setTargetMonthly(e.target.value); if (e.target.value) setTargetPct(''); }}
                  placeholder={`e.g. ${Math.round(actual * 0.7).toLocaleString()}`}
                  data-testid="ea-override-target-monthly"
                  className="w-full bg-white border border-indie-navy/15 px-2 py-1 text-sm num-tabular focus:outline-none focus:border-indie-navy"
                />
              </div>
              <div>
                <div className="text-[10px] text-indie-ink/55 mb-0.5">…or reduction %</div>
                <input
                  type="number"
                  value={targetPct}
                  onChange={(e) => { setTargetPct(e.target.value); if (e.target.value) setTargetMonthly(''); }}
                  placeholder="e.g. 20"
                  min="0"
                  max="100"
                  data-testid="ea-override-target-pct"
                  className="w-full bg-white border border-indie-navy/15 px-2 py-1 text-sm num-tabular focus:outline-none focus:border-indie-navy"
                />
              </div>
            </div>
            {previewOpp != null && (
              <div className="text-[11px] text-indie-evergreen font-semibold">
                Opp/mo preview: {moneyFull(previewOpp)} {actual ? `(from ${moneyFull(actual)}/mo actual)` : ''}
              </div>
            )}
            {!hasTarget && (
              <div className="text-[11px] text-indie-neg-dark">
                Without a target this saves as <strong>Pending Review</strong> and Opp/mo stays blank.
              </div>
            )}
          </div>
        )}

        <div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 200))}
            placeholder='Why this status? e.g. "Pause and measure lead drop"'
            rows={2}
            data-testid="ea-override-reason"
            className="w-full bg-indie-cream border border-indie-navy/15 px-2 py-1.5 text-sm focus:outline-none focus:border-indie-navy"
          />
          <div className="text-[10px] text-indie-ink/45 text-right">{reasonRemaining} chars left</div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-indie-ink/55 font-semibold">Initials</span>
          <input
            type="text"
            value={initials}
            onChange={(e) => setInitials(e.target.value.toUpperCase().slice(0, 4))}
            maxLength={4}
            data-testid="ea-override-initials"
            className="w-16 bg-indie-cream border border-indie-navy/15 px-2 py-1 text-sm font-semibold tracking-wider focus:outline-none focus:border-indie-navy"
          />
          <span className="text-[10px] text-indie-ink/45">auto from your name; editable</span>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={save}
            disabled={saving || !status}
            data-testid="ea-override-save"
            className="flex-1 bg-indie-ink text-indie-cream font-semibold uppercase tracking-wider text-xs px-3 py-2 hover:bg-indie-navy disabled:opacity-40"
          >
            {saving ? 'Saving…' : hasOverride ? 'Update override' : 'Set override'}
          </button>
          {hasOverride && (
            <button
              type="button"
              onClick={clear}
              disabled={saving}
              data-testid="ea-override-clear"
              className="px-3 py-2 text-xs uppercase tracking-wider font-semibold border border-indie-navy/20 text-indie-ink/70 hover:bg-indie-cream disabled:opacity-40"
            >
              Clear
            </button>
          )}
        </div>
        {hasOverride && existing.set_at && (
          <div className="text-[10px] text-indie-ink/50">
            Last saved by {existing.actor_initials || existing.set_by_name} on {existing.set_at?.slice(0, 10)}
            {existing.requested_status && existing.requested_status !== existing.manual_status && (
              <span className="ml-1 text-indie-neg-dark">
                · requested {existing.requested_status} → effective {existing.manual_status}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function WhyThisStatusPanel({ vendor }) {
  if (!vendor.suggested_status_rule) return null;
  return (
    <div className="border-t border-indie-navy/10 pt-3" data-testid="ea-why-this-status">
      <div className="label-editorial mb-1">Why this status?</div>
      <div className="text-[12px] text-indie-ink/80 mb-2 italic">{vendor.suggested_status_rule}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-indie-ink/70">
        <div><span className="text-indie-ink/50">Window total:</span> <span className="num-tabular font-semibold">{moneyFull(vendor.total_amount || 0)}</span></div>
        <div><span className="text-indie-ink/50">Months active:</span> <span className="num-tabular font-semibold">{vendor.months_active || 0}</span></div>
        <div><span className="text-indie-ink/50">Txn count:</span> <span className="num-tabular font-semibold">{vendor.txn_count || 0}</span></div>
        <div><span className="text-indie-ink/50">Cadence:</span> <span className="font-semibold">{vendor.cadence || '—'}</span></div>
        <div><span className="text-indie-ink/50">Contracted:</span> <span className="num-tabular font-semibold">{vendor.contracted_monthly ? moneyFull(vendor.contracted_monthly) + '/mo' : '—'}</span></div>
        <div><span className="text-indie-ink/50">Actual:</span> <span className="num-tabular font-semibold">{moneyFull(vendor.actual_monthly || 0)}/mo</span></div>
        {vendor.renewal_date && (
          <div className="col-span-2"><span className="text-indie-ink/50">Renews:</span> <span className="font-semibold">{vendor.renewal_date}{vendor.renewal_days_away != null ? ` (${vendor.renewal_days_away}d)` : ''}</span></div>
        )}
      </div>
    </div>
  );
}

function CoreInfrastructureToggle({ vendor, onSaved }) {
  const [saving, setSaving] = useState(false);
  const isCore = Boolean(vendor.is_core_infrastructure);
  const label = vendor.core_infrastructure_label;
  const toggle = async () => {
    setSaving(true);
    try {
      await api.post('/expense-audit/vendor-core-flag', {
        vendor_id: vendor.id,
        vendor_name: vendor.vendor_name,
        is_core_infrastructure: !isCore,
      });
      toast.success(!isCore
        ? `${vendor.vendor_name}: marked core infrastructure → Keep`
        : `${vendor.vendor_name}: core flag cleared`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save core flag.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="border-t border-indie-navy/10 pt-3" data-testid="ea-core-toggle">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="label-editorial">Core infrastructure</div>
          <div className="text-[11px] text-indie-ink/60 mt-0.5">
            {isCore
              ? `Flagged${label ? ` (${label})` : ''} — always defaults to Keep.`
              : 'Mark as core to lock this vendor to Keep regardless of utilization.'}
          </div>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={saving}
          data-testid="ea-core-toggle-btn"
          className={`relative inline-flex h-6 w-11 items-center transition-colors ${isCore ? 'bg-indie-evergreen' : 'bg-indie-navy/20'} disabled:opacity-50`}
        >
          <span className={`inline-block h-4 w-4 bg-white transition-transform ${isCore ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
    </div>
  );
}

function DecisionLogPanel({ vendorId, refreshKey, currentUser, onReset }) {
  const [log, setLog] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [resetting, setResetting] = useState(false);
  const isAdmin = currentUser?.email === 'joey@indielaw.com' || currentUser?.is_admin;

  useEffect(() => {
    if (!vendorId) { setLog([]); return; }
    api.get(`/expense-audit/decision-log?vendor_id=${vendorId}&limit=50`)
      .then((r) => setLog(r.data || []))
      .catch(() => setLog([]));
  }, [vendorId, refreshKey, resetting]);

  const resetLog = async () => {
    if (!window.confirm("Delete every decision log entry for this vendor? The vendor's overrides stay; only the audit trail is cleared.")) return;
    setResetting(true);
    try {
      const r = await api.post('/expense-audit/decision-log/reset', { vendor_id: vendorId });
      toast.success(`Deleted ${r.data?.deleted ?? 0} entries.`);
      setLog([]);
      onReset?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reset log.');
    } finally {
      setResetting(false);
    }
  };

  // Filter noise by default: only "set" and "status_changed" (real reviewer decisions).
  const visible = showAll ? log : log.filter((e) => e.action === 'set' || e.action === 'status_changed');
  if (!log.length) {
    return (
      <div className="border-t border-indie-navy/10 pt-3" data-testid="ea-decision-log">
        <div className="label-editorial mb-1">Decision log</div>
        <div className="text-[11px] text-indie-ink/45 italic">No decisions logged yet.</div>
      </div>
    );
  }
  return (
    <div className="border-t border-indie-navy/10 pt-3" data-testid="ea-decision-log">
      <div className="flex items-center justify-between mb-1">
        <div className="label-editorial">Decision log ({visible.length})</div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-[10px] text-indie-cobalt hover:text-indie-navy uppercase tracking-wider font-semibold"
            data-testid="ea-decision-log-toggle"
          >
            {showAll ? 'Hide edits' : `Show all (${log.length})`}
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={resetLog}
              disabled={resetting}
              className="text-[10px] text-indie-neg-dark hover:text-indie-ink uppercase tracking-wider font-semibold disabled:opacity-40"
              data-testid="ea-decision-log-reset"
            >
              {resetting ? '…' : 'Reset log'}
            </button>
          )}
        </div>
      </div>
      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
        {visible.map((e) => {
          const pal = STATUS_COLORS[e.to_status] || STATUS_COLORS.Watch;
          return (
            <div key={e.id} className="text-[11px] flex gap-2 items-start py-1 border-b border-indie-navy/5 last:border-0">
              <div className="font-semibold text-indie-ink/75 num-tabular whitespace-nowrap">
                {e.actor_initials || '??'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-indie-ink/50">{e.at?.slice(0, 10)}</span>
                  {e.from_status && (
                    <>
                      <span className="text-indie-ink/40">{e.from_status}</span>
                      <span className="text-indie-ink/40">→</span>
                    </>
                  )}
                  {e.to_status ? (
                    <span className={`inline-flex items-center gap-1 px-1 py-0.5 text-[9px] uppercase tracking-wider font-semibold ${pal.bg} ${pal.text}`}>
                      {e.to_status}
                    </span>
                  ) : (
                    <span className="text-indie-ink/55 italic">cleared</span>
                  )}
                  {e.target_monthly != null && <span className="text-indie-ink/55">target {moneyFull(e.target_monthly)}/mo</span>}
                  {e.target_reduction_pct != null && <span className="text-indie-ink/55">-{e.target_reduction_pct}%</span>}
                </div>
                {e.reason && <div className="text-indie-ink/65 italic mt-0.5">{e.reason}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SparkHistory({ history }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-indie-ink/55 mb-1">Monthly QBO spend</div>
      <div className="h-20">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={history} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <Line type="monotone" dataKey="amount" stroke="#1D57A5" strokeWidth={1.8} dot={{ r: 2, fill: '#1D57A5' }} />
            <XAxis dataKey="month_key" hide />
            <YAxis hide />
            <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => moneyFull(v)} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
